import { useEffect } from "react";

const SITE_NAME = "Carlo Bossi";
export const DEFAULT_TITLE = `${SITE_NAME}`;
// Same text as the description in public/index.html.
const DEFAULT_DESCRIPTION = "Carlo Bossi – sklep z perfumami. Odkryj bestsellery, nowości i rodziny zapachowe.";
const SITE_URL = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/+$/, "");

// Plain-text meta description (max ~155 chars) from a product/category text field.
export const toDescription = (text) => {
    const plain = String(text || "").replace(/<[^>]*>/g, " ").replace(/[*_#>`[\]]/g, "").replace(/\s+/g, " ").trim();
    if (plain.length <= 155) return plain;
    const cut = plain.slice(0, 155);
    return `${cut.slice(0, cut.lastIndexOf(" ") > 80 ? cut.lastIndexOf(" ") : cut.length)}…`;
};

// Finds the <head> tag matching `selector`, creating it from `attrs` when missing.
const headTag = (tagName, selector, attrs) => {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement(tagName);
        Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
        document.head.appendChild(el);
    }
    return el;
};

const setMeta = (attr, key, content) => {
    const selector = `meta[${attr}="${key}"]`;
    if (!content) {
        document.head.querySelector(selector)?.remove();
        return;
    }
    headTag("meta", selector, { [attr]: key }).setAttribute("content", content);
};

/**
 * Keeps <head> in sync with the current page during client-side navigation:
 * title, description, canonical URL, Open Graph tags, robots and JSON-LD.
 * The backend (/api/seo/page) renders the same tags into the first HTML for
 * product and category pages, so link previews work without JS.
 */
export const usePageMeta = ({ title, description, image, path, type = "website", jsonLd, noindex = false }) => {
    const jsonLdText = jsonLd ? JSON.stringify(jsonLd) : "";

    useEffect(() => {
        const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
        const desc = description || DEFAULT_DESCRIPTION;
        const url = SITE_URL + (path ?? window.location.pathname);

        document.title = fullTitle;
        setMeta("name", "description", desc);
        setMeta("name", "robots", noindex ? "noindex, nofollow" : null);
        headTag("link", 'link[rel="canonical"]', { rel: "canonical" }).setAttribute("href", url);
        setMeta("property", "og:type", type);
        setMeta("property", "og:title", fullTitle);
        setMeta("property", "og:description", desc);
        setMeta("property", "og:url", url);
        setMeta("property", "og:image", image);
        setMeta("name", "twitter:card", image ? "summary_large_image" : "summary");

        const script = document.head.querySelector("script[data-page-jsonld]");
        if (jsonLdText) {
            headTag("script", "script[data-page-jsonld]", { type: "application/ld+json", "data-page-jsonld": "" })
                .textContent = jsonLdText;
        } else {
            script?.remove();
        }
    }, [title, description, image, path, type, jsonLdText, noindex]);
};

// Tab title only; the rest of the tags fall back to the shop defaults.
const usePageTitle = (title, options) => usePageMeta({ title, ...options });

export default usePageTitle;
